import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {HttpClient, HttpParams} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {IDynamic} from '../../main/modules/dynamic/models/IDynamic';
import {FormSchema} from '@shared/widgets/models/schema';
import {LocalToUtcPipe} from '@shared/pipes/local-to-utc.pipe';
import {config} from '../../app.config';
import {DynamicModuleContext} from './dynamic-module-context';
import {toUtcForApi} from '../datetime/datetime-boundary';

@Injectable()
export class DynamicService {

  private _table = '';
  private _moduleId = '';
  private _schema: FormSchema;
  private dataStore: {
    items: IDynamic[]
  };
  private _items: BehaviorSubject<IDynamic[]>;

  constructor(private http: HttpClient, private localToUtc: LocalToUtcPipe) {
    this.dataStore = {items: []};
    this._items = new BehaviorSubject<IDynamic[]>([]);
  }

  /**
   * @deprecated Prefer passing DynamicModuleContext into find/save/create/getById (AC-32).
   * Kept for existing resolvers/components until full cutover.
   */
  set moduleId(moduleId: string) {
    this._moduleId = moduleId;
  }

  get moduleId(): string {
    return this._moduleId;
  }

  /** @deprecated Prefer DynamicModuleContext.table */
  set table(tableName: string) {
    this._table = tableName;
  }

  get table(): string {
    return this._table;
  }

  /** @deprecated Prefer DynamicModuleContext.schema */
  set schema(schema: FormSchema) {
    this._schema = schema;
  }

  get schema(): FormSchema {
    return this._schema;
  }

  get items(): Observable<IDynamic[]> {
    return this._items.asObservable();

  }

  /** Resolve moduleId for a call — explicit context wins over mutable field. */
  resolveModuleId(ctx?: DynamicModuleContext | string): string {
    if (typeof ctx === 'string') {
      return ctx;
    }
    return ctx?.moduleId || this._moduleId;
  }

  resolveSchema(ctx?: DynamicModuleContext): FormSchema | null | undefined {
    return ctx?.schema !== undefined ? ctx.schema : this._schema;
  }

  find(
    filter = '',
    exactFilter = false,
    ownerId = '',
    searchParams = '',
    searchRange = '',
    searchRangeColumn = '',
    archive = false,
    sortBy = '',
    sortOrder = 'asc',
    pageNumber = 0,
    pageSize = config.screenListItemsCount,
    creditFilter = false,
    clientState = false,
    report = false,
    ctx?: DynamicModuleContext | string,
  ): Observable<any> {
    const moduleId = this.resolveModuleId(ctx);
    const params = new HttpParams()
      .set(filter ? 'filter' : '', filter)
      .set('exactFilter', `${exactFilter}`)
      .set(ownerId ? 'ownerId' : '', ownerId)
      .set(searchParams ? 'searchParams' : '', searchParams)
      .set(searchRange ? 'searchRange' : '', searchRange)
      .set(searchRangeColumn ? 'searchRangeColumn' : '', searchRangeColumn)
      .set(sortBy ? 'sortBy' : '', sortBy)
      .set(sortOrder ? 'sortOrder' : '', sortOrder)
      .set('creditFilter', creditFilter ? '1' : '0')
      .set('pageNumber', pageNumber.toString())
      .set('pageSize', pageSize.toString())
      .set('archive', archive ? '1' : '0')
      .set('pastClients', clientState ? '1' : '0');

    const options = {
      params,
    };

    if (report) {
      (options as any).responseType = 'arraybuffer';
    }
    return this.http.get(`/${report ? 'report' : 'data'}/${moduleId}`, options);
  }

  getById(id: string, ctx?: DynamicModuleContext | string): Observable<IDynamic> {
    const moduleId = this.resolveModuleId(ctx);
    return this.http.get<IDynamic>(`/data/${moduleId}/${id}`);
  }

  getFirstByParams(
    sortBy: string,
    sort: string,
    ownerId: string,
    ctx?: DynamicModuleContext | string,
  ): Observable<IDynamic> {
    const moduleId = this.resolveModuleId(ctx);

    const params = new HttpParams()
      .set(ownerId ? 'ownerId' : '', ownerId)
      .set(sortBy ? 'sortBy' : '', sortBy)
      .set(sort ? 'sortOrder' : '', sort)
      .set('limit', '1')

    const options = {
      params,
    };

    return this.http.get<IDynamic>(`/data/${moduleId}`, options);
  }

  save(dynamic: IDynamic, ctx?: DynamicModuleContext | string): Observable<IDynamic> {
    const moduleId = this.resolveModuleId(ctx);
    const schema = typeof ctx === 'string' ? this._schema : this.resolveSchema(ctx);
    const url = `/data/${moduleId}/${dynamic.id}`;
    const dataToSave = this.localTimeToUtc(dynamic, schema);
    return this.http.put<IDynamic>(url, dataToSave);
  }

  create(dynamic: IDynamic, ctx?: DynamicModuleContext | string): Observable<IDynamic> {
    const moduleId = this.resolveModuleId(ctx);
    const schema = typeof ctx === 'string' ? this._schema : this.resolveSchema(ctx);
    const url = `/data/${moduleId}`;
    const dataToSave = this.localTimeToUtc(dynamic, schema);
    return this.http.post<IDynamic>(url, dataToSave);
  }

  localTimeToUtc(dynamic: IDynamic, schema?: FormSchema | null) {
    const data = {...dynamic};
    const sch = schema !== undefined ? schema : this._schema;
    if (data.createdAt) {
      data.createdAt = toUtcForApi(data.createdAt, this.localToUtc) as string;
    }

    if (data.lastEditedAt) {
      data.lastEditedAt = toUtcForApi(data.lastEditedAt, this.localToUtc) as string;
    }

    if (sch?.properties) {
      const fieldsToConvert = Object.entries(sch.properties)
        .filter(([key, value]) => value.format === 'datetime' && !['createdAt', 'lastEditedAt'].includes(key))
        .map(([key]) => key);

      fieldsToConvert.forEach(field => {
        data[field] = toUtcForApi(data[field], this.localToUtc) as any;
      });
    }

    return data;
  }

  detectConflicts(id: string, originalData: any, schema: FormSchema, ctx?: DynamicModuleContext | string): Observable<number> {
    const fieldsToCheck = Object.keys(schema?.properties || {})
      .filter(key => schema.properties[key].checkConflict);


    if (fieldsToCheck.length === 0) {
      return of(0);
    }

    return this.getById(id, ctx).pipe(
      map(latestItem => {
        let conflictCount = 0;
        fieldsToCheck.forEach(field => {
          if (latestItem[field] !== originalData[field]) {
            conflictCount++;
          }
        });
        return conflictCount;
      })
    );
  }
}
